import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, X, Image as ImageIcon, Play } from 'lucide-react';

/**
 * BusinessMedia - Premium cover image and gallery with delightful interactions
 */
export function BusinessMedia({ coverImage, images = [], onImageClick }) {
    const [imageLoading, setImageLoading] = useState(true);
    const [selectedImageIndex, setSelectedImageIndex] = useState(null);

    const handleImageClick = (index) => {
        setSelectedImageIndex(index);
        onImageClick?.(index);
    };

    const handleNextImage = () => {
        setSelectedImageIndex((prev) => (prev + 1) % images.length);
    };

    const handlePrevImage = () => {
        setSelectedImageIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    const isVideo = (url) => url?.toLowerCase().includes('.mp4');

    return (
        <>
            {/* Hero Cover Image/Video with elegant styling */}
            {coverImage && (
                <motion.div
                    initial={{ opacity: 0, scale: 1.05 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                    className="relative w-full aspect-video overflow-hidden bg-gradient-to-br from-surface-muted to-surface-sunken"
                >
                    {imageLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-warning-50 to-warning-50">
                            <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                className="w-12 h-12 border-4 border-warning-100 border-t-amber-500 rounded-full"
                            />
                        </div>
                    )}

                    {isVideo(coverImage) ? (
                        <video
                            src={coverImage}
                            className="w-full h-full object-cover"
                            controls
                            playsInline
                            preload="metadata"
                            onLoadedData={() => setImageLoading(false)}
                        >
                            <source src={coverImage} type="video/mp4" />
                            הדפדפן שלך לא תומך בהצגת וידאו
                        </video>
                    ) : (
                        <img
                            src={coverImage}
                            alt="Business Cover"
                            className={`w-full h-full object-cover transition-all duration-700 ${imageLoading ? 'opacity-0 scale-105' : 'opacity-100 scale-100'
                                }`}
                            onLoad={() => setImageLoading(false)}
                            onError={() => {
                                console.warn('⚠️ Failed to load cover image.');
                                setImageLoading(false);
                            }}
                        />
                    )}
                    {/* Elegant overlay gradient */}
                    <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
                </motion.div>
            )}

            {/* Premium Gallery - Horizontal Scroll with refined design */}
            {images && images.length > 0 && (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="px-6 py-6 bg-white border-b border-line-subtle"
                >
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-bold text-ink-strong tracking-tight">
                            גלריה
                        </h3>
                        <span className="text-xs font-semibold text-ink-subtle bg-surface-muted px-3 py-1 rounded-full">
                            {images.length} תמונות
                        </span>
                    </div>
                    <div className="flex gap-4 overflow-x-auto pb-3 scrollbar-hide">
                        {images.map((url, idx) => {
                            const isVid = isVideo(url);
                            return (
                                <motion.div
                                    key={idx}
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.3 + idx * 0.05 }}
                                    whileHover={{ y: -4 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => handleImageClick(idx)}
                                    className="relative flex-shrink-0 w-36 h-36 rounded-2xl overflow-hidden bg-gradient-to-br from-surface-muted to-surface-sunken cursor-pointer shadow-md hover:shadow-2xl transition-all duration-300 group"
                                >
                                    {isVid ? (
                                        <>
                                            <video
                                                src={url}
                                                className="w-full h-full object-cover"
                                                playsInline
                                            />
                                            <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/20 transition-colors">
                                                <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                                                    <Play className="w-6 h-6 text-ink-strong ml-0.5" fill="currentColor" />
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <img
                                            src={url}
                                            alt={`Gallery ${idx + 1}`}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                        />
                                    )}
                                    {/* Hover overlay */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                </motion.div>
                            );
                        })}
                    </div>
                </motion.div>
            )}

            {/* Elegant No Images Placeholder */}
            {!coverImage && (!images || images.length === 0) && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="w-full aspect-video bg-gradient-to-br from-warning-50 to-warning-50 flex flex-col items-center justify-center gap-3"
                >
                    <ImageIcon className="w-20 h-20 text-warning-100" strokeWidth={1.5} />
                    <p className="text-sm font-medium text-ink-subtle">אין תמונות זמינות</p>
                </motion.div>
            )}

            {/* Premium Fullscreen Image Modal */}
            <AnimatePresence>
                {selectedImageIndex !== null && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/95 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={() => setSelectedImageIndex(null)}
                    >
                        <motion.div
                            initial={{ scale: 0.85, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.85, opacity: 0 }}
                            transition={{ type: "spring", damping: 25, stiffness: 300 }}
                            className="relative w-full max-w-5xl"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {isVideo(images[selectedImageIndex]) ? (
                                <video
                                    src={images[selectedImageIndex]}
                                    controls
                                    autoPlay
                                    className="w-full max-h-[85vh] object-contain rounded-3xl shadow-2xl"
                                    style={{ background: '#1a1a1a' }}
                                >
                                    <source src={images[selectedImageIndex]} type="video/mp4" />
                                    הדפדפן שלך לא תומך בהצגת וידאו
                                </video>
                            ) : (
                                <img
                                    src={images[selectedImageIndex]}
                                    alt="Full View"
                                    className="w-full max-h-[85vh] object-contain rounded-3xl shadow-2xl"
                                />
                            )}

                            {/* Elegant Close Button */}
                            <motion.button
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: 0.2 }}
                                onClick={() => setSelectedImageIndex(null)}
                                className="absolute -top-2 -right-2 w-12 h-12 bg-white hover:bg-surface-muted rounded-full flex items-center justify-center shadow-xl transition-all hover:scale-110 active:scale-95"
                            >
                                <X className="w-6 h-6 text-ink-strong" strokeWidth={2.5} />
                            </motion.button>

                            {/* Refined Navigation Arrows */}
                            {images.length > 1 && (
                                <>
                                    <motion.button
                                        initial={{ x: -20, opacity: 0 }}
                                        animate={{ x: 0, opacity: 1 }}
                                        transition={{ delay: 0.15 }}
                                        onClick={handlePrevImage}
                                        className="absolute left-4 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/95 hover:bg-white rounded-full flex items-center justify-center shadow-xl transition-all hover:scale-110 active:scale-95"
                                    >
                                        <ChevronRight className="w-7 h-7 text-ink-strong" strokeWidth={2.5} />
                                    </motion.button>
                                    <motion.button
                                        initial={{ x: 20, opacity: 0 }}
                                        animate={{ x: 0, opacity: 1 }}
                                        transition={{ delay: 0.15 }}
                                        onClick={handleNextImage}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/95 hover:bg-white rounded-full flex items-center justify-center shadow-xl transition-all hover:scale-110 active:scale-95"
                                    >
                                        <ChevronLeft className="w-7 h-7 text-ink-strong" strokeWidth={2.5} />
                                    </motion.button>
                                </>
                            )}

                            {/* Image Counter */}
                            <motion.div
                                initial={{ y: 20, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: 0.2 }}
                                className="absolute -bottom-12 left-1/2 -translate-x-1/2 px-4 py-2 bg-white/95 rounded-full shadow-lg"
                            >
                                <span className="text-sm font-semibold text-ink-strong">
                                    {selectedImageIndex + 1} / {images.length}
                                </span>
                            </motion.div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}

export default BusinessMedia;
